import React, { useState } from 'react';
import { 
  BookOpen, Users, ShoppingCart, Truck, Shield, ChevronRight,
  Search, CreditCard, Package, MapPin, Bell, Settings, Camera,
  Barcode, ClipboardList, Navigation, CheckCircle, Phone, Star,
  LayoutDashboard, FileText, BarChart3, UserCog, Store, Gift
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const AppUsersPortal = ({ onBack, userRole }) => {
  const [selectedGuide, setSelectedGuide] = useState(userRole || 'customer');

  const guides = {
    customer: {
      title: 'Customer Guide',
      icon: ShoppingCart,
      color: 'blue',
      description: 'Learn how to shop, order, and track deliveries',
      sections: [
        {
          title: 'Getting Started',
          icon: Users,
          steps: [
            { title: 'Create an Account', description: 'Click "Sign In" → "Create Account" → Fill in your details → Verify your email' },
            { title: 'Complete Your Profile', description: 'Add your delivery address, phone number, and preferences' },
            { title: 'Browse Products', description: 'Explore categories or use the search bar to find products' },
          ]
        },
        {
          title: 'Shopping',
          icon: ShoppingCart,
          steps: [
            { title: 'Add to Cart', description: 'Click the "+" button on any product to add it to your cart' },
            { title: 'View Cart', description: 'Click the cart icon in the top right to see your items' },
            { title: 'Adjust Quantities', description: 'Use +/- buttons to change quantities or remove items' },
            { title: 'AI Picture Search', description: 'Use "Pictures2Cart" to search products by taking a photo!' },
          ]
        },
        {
          title: 'Checkout Process',
          icon: CreditCard,
          steps: [
            { title: 'Review Cart', description: 'Verify items and quantities in your cart' },
            { title: 'Choose Order Type', description: 'Select "Delivery" or "Store Pickup"' },
            { title: 'Enter Delivery Info', description: 'Provide your address and select delivery speed' },
            { title: 'Select Payment', description: 'Choose Card, Gift Card, E-Transfer, or Cash (pickup only)' },
            { title: 'Confirm Order', description: 'Review totals and place your order' },
          ]
        },
        {
          title: 'Delivery Options',
          icon: Truck,
          steps: [
            { title: 'Priority (2-4 hours)', description: 'Fastest delivery option for urgent orders' },
            { title: 'Same Day', description: 'Receive your order by end of business day' },
            { title: 'Next Day', description: 'Delivery by end of next business day' },
            { title: 'Standard (3-5 days)', description: 'Most economical delivery option' },
          ]
        },
        {
          title: 'Order Tracking',
          icon: MapPin,
          steps: [
            { title: 'My Orders', description: 'View all your orders in "My Orders" section' },
            { title: 'Real-Time Tracking', description: 'Track your delivery live on the map when driver is en route' },
            { title: 'Notifications', description: 'Receive updates at each stage: picking, shipped, out for delivery, delivered' },
            { title: 'Proof of Delivery', description: 'View delivery photos in your order details' },
          ]
        },
        {
          title: 'Rewards Program',
          icon: Gift,
          steps: [
            { title: 'Shop Regularly', description: 'Enjoy great products and build your order history' },
            { title: 'Track Orders', description: 'Use the My Orders section to track all your purchases' },
            { title: 'Save Favorites', description: 'Heart your favorite items for quick reordering' },
            { title: 'Get Notifications', description: 'Stay updated on special offers and promotions' },
          ]
        },
      ]
    },
    driver: {
      title: 'Driver Guide',
      icon: Truck,
      color: 'green',
      description: 'Learn how to accept, deliver, and complete orders',
      sections: [
        {
          title: 'Getting Started',
          icon: Users,
          steps: [
            { title: 'Driver Account', description: 'Your account must be set up as a Driver role by admin' },
            { title: 'Login', description: 'Sign in with your driver credentials' },
            { title: 'Driver Dashboard', description: 'Access delivery features from the main menu' },
          ]
        },
        {
          title: 'Available Deliveries',
          icon: ClipboardList,
          steps: [
            { title: 'View Available Orders', description: 'See orders ready for delivery assignment' },
            { title: 'Order Details', description: 'View customer address, items, and delivery notes' },
            { title: 'Accept Delivery', description: 'Click "Accept" to assign the order to yourself' },
            { title: 'Multiple Orders', description: 'You can accept multiple orders for efficient routing' },
          ]
        },
        {
          title: 'Delivery Process',
          icon: Navigation,
          steps: [
            { title: 'Head to Store', description: 'Click "Heading to Store" when you start driving' },
            { title: 'Arrive at Store', description: 'Click "Arrived at Store" when you reach the pickup location' },
            { title: 'Collect Order', description: 'Show your driver ID and collect the order package' },
            { title: 'Start Delivery', description: 'Click "Out for Delivery" when leaving the store' },
          ]
        },
        {
          title: 'Completing Delivery',
          icon: Camera,
          steps: [
            { title: 'Navigate to Customer', description: 'Use built-in navigation or your preferred maps app' },
            { title: 'Deliver Package', description: 'Hand over to customer or leave at door per instructions' },
            { title: 'Take Proof Photos', description: 'Capture 1-2 photos showing package at delivery location' },
            { title: 'Complete Delivery', description: 'Upload photos and mark as delivered' },
          ]
        },
        {
          title: 'My Deliveries',
          icon: Package,
          steps: [
            { title: 'Active Deliveries', description: 'View your current assigned deliveries' },
            { title: 'Delivery History', description: 'See all your completed deliveries' },
            { title: 'Earnings', description: 'Track your delivery statistics and performance' },
            { title: 'Customer Contact', description: 'Call or message customers if needed' },
          ]
        },
        {
          title: 'Best Practices',
          icon: Star,
          steps: [
            { title: 'Handle with Care', description: 'Use insulated bags for perishable items' },
            { title: 'Be Professional', description: 'Greet customers courteously, wear ID badge' },
            { title: 'Follow Instructions', description: 'Check delivery notes for special instructions' },
            { title: 'Report Issues', description: 'Contact dispatch immediately for any problems' },
          ]
        },
      ]
    },
    admin: {
      title: 'Administrator Guide',
      icon: Shield,
      color: 'purple',
      description: 'Manage products, orders, users, and business operations',
      sections: [
        {
          title: 'Dashboard Overview',
          icon: LayoutDashboard,
          steps: [
            { title: 'Quick Stats', description: 'View daily sales, orders, and key metrics at a glance' },
            { title: 'Recent Orders', description: 'Monitor latest orders and their status' },
            { title: 'Alerts', description: 'See low stock warnings and pending actions' },
          ]
        },
        {
          title: 'Product Management',
          icon: Package,
          steps: [
            { title: 'Add Products', description: 'Click "Add Product" → Fill details → Upload image → Save' },
            { title: 'Edit Products', description: 'Click edit icon on any product to modify details' },
            { title: 'AI Image Refinement', description: 'After uploading, select AI options to enhance product photos' },
            { title: 'Barcode Scanner', description: 'Use camera to scan barcodes for quick product lookup' },
            { title: 'Stock Management', description: 'Toggle stock status on/off for each product' },
          ]
        },
        {
          title: 'Order Management',
          icon: ClipboardList,
          steps: [
            { title: 'Orders Portal', description: 'View all orders with filtering and search' },
            { title: 'Order Status', description: 'Update order status through the fulfillment workflow' },
            { title: 'Picking List', description: 'Access picking lists for order preparation' },
            { title: 'Assign Drivers', description: 'Assign delivery orders to available drivers' },
            { title: 'Archive Orders', description: 'View completed orders by day, week, month, year' },
          ]
        },
        {
          title: 'Payment Verification',
          icon: CreditCard,
          steps: [
            { title: 'Payment Portal', description: 'Access the payment confirmation portal' },
            { title: 'E-Transfer Verification', description: 'Review e-transfer confirmation numbers and screenshots' },
            { title: 'Approve/Reject', description: 'Verify payments and update order status' },
            { title: 'Add Notes', description: 'Add verification notes for record keeping' },
          ]
        },
        {
          title: 'User Management',
          icon: UserCog,
          steps: [
            { title: 'View Users', description: 'See all registered customers, drivers, and admins' },
            { title: 'Edit Roles', description: 'Change user roles (customer, driver, admin)' },
            { title: 'Account Status', description: 'Activate or deactivate user accounts' },
            { title: 'Driver Assignment', description: 'Set up new driver accounts' },
          ]
        },
        {
          title: 'Reports & Analytics',
          icon: BarChart3,
          steps: [
            { title: 'Sales Reports', description: 'View daily, weekly, monthly sales data' },
            { title: 'Product Performance', description: 'See best sellers and slow-moving inventory' },
            { title: 'Customer Analytics', description: 'Track customer behavior and order patterns' },
            { title: 'Export Data', description: 'Download reports in CSV format' },
          ]
        },
        {
          title: 'System Settings',
          icon: Settings,
          steps: [
            { title: 'Delivery Zones', description: 'Configure delivery areas and pricing' },
            { title: 'Delivery Types', description: 'Set up delivery speed options and fees' },
            { title: 'Notifications', description: 'Configure email and push notification settings' },
            { title: 'Business Parameters', description: 'Set costs for profitability calculations' },
          ]
        },
      ]
    }
  };

  const currentGuide = guides[selectedGuide];
  const GuideIcon = currentGuide.icon;

  const colorClasses = {
    blue: {
      bg: 'bg-blue-500',
      bgLight: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      badge: 'bg-blue-100 text-blue-700'
    },
    green: {
      bg: 'bg-green-500',
      bgLight: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700',
      badge: 'bg-green-100 text-green-700'
    },
    purple: {
      bg: 'bg-purple-500',
      bgLight: 'bg-purple-50',
      border: 'border-purple-200',
      text: 'text-purple-700',
      badge: 'bg-purple-100 text-purple-700'
    }
  };

  const colors = colorClasses[currentGuide.color];

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {onBack && (
        <Button variant="ghost" onClick={onBack} className="mb-4">
          ← Back
        </Button>
      )}

      {/* Header */}
      <div className="text-center mb-8">
        <div className={`w-16 h-16 ${colors.bg} rounded-full flex items-center justify-center mx-auto mb-4`}>
          <BookOpen className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-800">App Users Portal</h1>
        <p className="text-gray-500 mt-2">Learn how to use UPLANDS FOOD MARKET</p>
      </div>

      {/* Role Selector */}
      <div className="flex justify-center gap-3 mb-8">
        {Object.entries(guides).map(([key, guide]) => {
          const Icon = guide.icon;
          const isSelected = selectedGuide === key;
          return (
            <button
              key={key}
              onClick={() => setSelectedGuide(key)}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                isSelected 
                  ? `${colorClasses[guide.color].bg} text-white border-transparent shadow-lg` 
                  : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium">{guide.title}</span>
            </button>
          );
        })}
      </div>

      {/* Guide Content */}
      <Card className={`overflow-hidden ${colors.border} border-2`}>
        {/* Guide Header */}
        <div className={`${colors.bgLight} p-6`}>
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 ${colors.bg} rounded-full flex items-center justify-center`}>
              <GuideIcon className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{currentGuide.title}</h2>
              <p className="text-gray-600">{currentGuide.description}</p>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="p-6 space-y-6">
          {currentGuide.sections.map((section, sectionIdx) => {
            const SectionIcon = section.icon;
            return (
              <div key={sectionIdx} className="border rounded-xl overflow-hidden">
                <div className={`${colors.bgLight} px-4 py-3 flex items-center gap-3`}>
                  <SectionIcon className={`w-5 h-5 ${colors.text}`} />
                  <h3 className="font-bold text-gray-800">{section.title}</h3>
                </div>
                <div className="divide-y">
                  {section.steps.map((step, stepIdx) => (
                    <div key={stepIdx} className="p-4 flex items-start gap-4 hover:bg-gray-50 transition-colors">
                      <Badge className={`${colors.badge} flex-shrink-0`}>
                        {stepIdx + 1}
                      </Badge>
                      <div>
                        <p className="font-medium text-gray-800">{step.title}</p>
                        <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className={`${colors.bgLight} p-6 border-t`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-600">
              <Phone className="w-4 h-4" />
              <span className="text-sm">Need help? Contact support@uplandsfoodmarket.com</span>
            </div>
            <Badge className={colors.badge}>
              Last updated: January 2026
            </Badge>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AppUsersPortal;
